import { RootState } from "core-integration/src/store";
import { getActiveChip } from "core-integration/src/store/chips/reducers";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { IPickLight } from "core-integration/src/store/entries/types";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import { getSavedPicks } from "core-integration/src/store/squad/reducers";
import ElementCardFixtures from "plos/src/components/ElementCard/ElementCardFixtures";
import ElementDetailsBar from "plos/src/components/ElementCard/ElementDetailsBar";
import ValueForPlayedElement from "plos/src/components/ElementCard/ValueForPlayedElement";
import { visuallyHidden } from "plos/src/styles/utils.css";
import { integerToMoneyWithCurrency } from "plos/src/utils/money";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useOverlayContext } from "../../../contexts/OverlayContext";
import { SelectedOverlay } from "../types";
import ElementOverlayFDR from "./ElementOverlayFDR";
import {
  overlayValueAnimation,
  ownershipDirectionWrapper,
} from "./elementOverlayValue.css";
import { OwnershipIcon } from "./OwnershipIcon";
import { PriceChangeOverlay } from "./PriceChangeOverlay";

interface ElementOverlayValueProps {
  pick: IPickLight;
  eventId?: number;
  elementId?: number;
}

const ElementOverlayValue = ({
  pick,
  eventId,
  elementId,
}: ElementOverlayValueProps) => {
  const { selectedOverlay, overlayPage } = useOverlayContext();
  const { t } = useTranslation("common");
  // Track the overlay value to determine if it has changed, which will trigger the animation
  const prevOverlayRef = useRef<SelectedOverlay>(selectedOverlay);
  const hasOverlayChanged = prevOverlayRef.current !== selectedOverlay;

  const currentEvent = useSelector(getCurrentEvent);

  useEffect(() => {
    prevOverlayRef.current = selectedOverlay;
  }, [selectedOverlay]);

  const elementsById = useSelector((state: RootState) =>
    getElementsById(state, undefined, getActiveChip(state)?.id),
  );
  const savedPicks = useSelector(getSavedPicks);

  const element = elementsById[elementId || pick.element];

  if (!element) {
    return null;
  }

  // On pick team and transfers, show points from the current event, otherwise use the event passed to the component
  const pointsEventId =
    currentEvent?.id && (overlayPage === "myTeam" || overlayPage === "squad")
      ? currentEvent?.id
      : eventId;

  let content: ReactNode;
  switch (selectedOverlay) {
    case "opponent":
      content = (
        <ElementCardFixtures eventId={eventId} elementId={element.id} />
      );
      break;
    case "points":
      content = pointsEventId ? (
        <ValueForPlayedElement eventId={pointsEventId} pick={pick} />
      ) : overlayPage === "dreamTeam" && !eventId ? (
        <ElementDetailsBar>{element.total_points}</ElementDetailsBar>
      ) : (
        <ElementDetailsBar>-</ElementDetailsBar>
      );
      break;
    case "currentPrice":
      content = (
        <ElementDetailsBar>
          <span className={visuallyHidden}>
            {t("pitchControls.pitchOverlay.currentPrice", "Current Price")}
          </span>
          {integerToMoneyWithCurrency(element.now_cost, 10)}
        </ElementDetailsBar>
      );
      break;
    case "sellingPrice": {
      // savedPicks is keyed by squad order, not pick.position, so match on element
      const savedPick = Object.values(savedPicks).find(
        (p) => p.element === element.id,
      );
      content = (
        <ElementDetailsBar>
          <span className={visuallyHidden}>
            {t("pitchControls.pitchOverlay.sellingPrice", "Selling Price")}
          </span>
          {savedPick
            ? integerToMoneyWithCurrency(savedPick.selling_price, 10)
            : integerToMoneyWithCurrency(element.now_cost, 10)}
        </ElementDetailsBar>
      );
      break;
    }
    case "fdr":
      content = <ElementOverlayFDR element={element} />;
      break;
    case "form":
      content = (
        <ElementDetailsBar>
          <span className={visuallyHidden}>
            {t("pitchControls.pitchOverlay.form", "Form")}
          </span>
          {element.form}
        </ElementDetailsBar>
      );
      break;
    case "ownership":
      content = (
        <ElementDetailsBar>
          <span className={visuallyHidden}>
            {t("pitchControls.pitchOverlay.ownership", "Ownership")}
          </span>
          <div className={ownershipDirectionWrapper}>
            {element.selected_by_percent}%
            <OwnershipIcon
              transfersIn={element.transfers_in_event}
              transfersOut={element.transfers_out_event}
            />
          </div>
        </ElementDetailsBar>
      );
      break;
    case "price_change":
      content = <PriceChangeOverlay element={element} />;
      break;
    default:
      content = null;
  }

  if (!content) {
    return null;
  }

  return (
    <div
      key={selectedOverlay}
      className={hasOverlayChanged ? overlayValueAnimation : undefined}
    >
      {content}
    </div>
  );
};

export default ElementOverlayValue;
